import React, { useMemo, useState } from "react";
import { Eye, Pencil, Plus, Power, RefreshCw, Users } from "lucide-react";
import { MessageRow, Mono, TableCard, Td, Th, Toolbar, rowClass, tableClass, tbodyClass, theadClass } from "../ui/DataPage";
import GroupFormModal from "./GroupFormModal";
import { useStatusToggle } from "./StatusToggle";
import {
  Button,
  ConfirmDialog,
  DetailGrid,
  EmptyState,
  EntityStatus,
  FilterSelect,
  IconButton,
  Overlay,
  Pagination,
  SearchInput,
  YesNo,
  formatDate,
  usePagination,
} from "./ui";

const COLS = 9;

export default function GroupsTab({ groups, isLoading, error, refetch, onGroupSaved }) {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [settlement, setSettlement] = useState("all");
  const [editing, setEditing] = useState(undefined); // undefined = closed, null = new, object = edit
  const [viewing, setViewing] = useState(null);

  const toggle = useStatusToggle({
    entity: "group",
    describe: (g) => ({
      id: g.group_id,
      next: g.status === "active" ? "inactive" : "active",
      label: `Group ${g.group_id} · ${g.group_name}`,
      warning: "Its locations and stations keep their own status.",
    }),
    onChanged: () => refetch(),
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return groups.filter((g) => {
      if (status !== "all" && g.status !== status) return false;
      if (settlement !== "all" && String(g.settlement_enabled) !== settlement) return false;
      if (!q) return true;
      return [g.group_id, g.group_name, g.owner_name, g.contact_email, g.contact_phone].some((v) => String(v || "").toLowerCase().includes(q));
    });
  }, [groups, search, status, settlement]);

  const pager = usePagination(filtered, 10, `${search}|${status}|${settlement}`);
  const hasFilters = search || status !== "all" || settlement !== "all";

  return (
    <div className="space-y-4">
      <Toolbar>
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-1 flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
            <SearchInput value={search} onChange={setSearch} placeholder="Search ID, name, owner, email, phone" />
            <FilterSelect label="Status" value={status} onChange={setStatus} options={[{ value: "all", label: "All statuses" }, { value: "active", label: "Active" }, { value: "inactive", label: "Inactive" }]} />
            <FilterSelect label="Settlement" value={settlement} onChange={setSettlement} options={[{ value: "all", label: "Any settlement" }, { value: "true", label: "Settlement on" }, { value: "false", label: "Settlement off" }]} />
          </div>
          <div className="flex gap-2">
            <Button icon={RefreshCw} onClick={refetch} disabled={isLoading}>Refresh</Button>
            <Button variant="primary" icon={Plus} onClick={() => setEditing(null)}>Add group</Button>
          </div>
        </div>
      </Toolbar>

      <TableCard header={<span>{filtered.length} group{filtered.length === 1 ? "" : "s"}</span>} footer={filtered.length > 0 ? <Pagination {...pager} /> : null}>
        <table className={tableClass}>
          <thead className={theadClass}>
            <tr>
              <Th>Group ID</Th>
              <Th>Group name</Th>
              <Th>Owner</Th>
              <Th>Contact email</Th>
              <Th>Contact phone</Th>
              <Th>Settlement</Th>
              <Th>Status</Th>
              <Th>Created</Th>
              <Th align="right">Actions</Th>
            </tr>
          </thead>
          <tbody className={tbodyClass}>
            {isLoading && groups.length === 0 ? (
              <MessageRow colSpan={COLS}>Loading groups…</MessageRow>
            ) : error ? (
              <MessageRow colSpan={COLS} tone="error">{error} · <button className="underline" onClick={refetch}>Retry</button></MessageRow>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={COLS}>
                  <EmptyState
                    icon={Users}
                    title={hasFilters ? "No groups match these filters" : "No groups yet"}
                    description={hasFilters ? "Try a different search or clear the filters." : "Create the first group to start adding locations and stations."}
                    action={!hasFilters && <Button variant="primary" icon={Plus} onClick={() => setEditing(null)}>Add group</Button>}
                  />
                </td>
              </tr>
            ) : (
              pager.slice.map((g) => (
                <tr key={g.group_id} className={rowClass}>
                  <Td><Mono>{g.group_id}</Mono></Td>
                  <Td className="font-medium text-neutral-900">{g.group_name}</Td>
                  <Td>{g.owner_name || <span className="text-neutral-400">—</span>}</Td>
                  <Td>{g.contact_email || <span className="text-neutral-400">—</span>}</Td>
                  <Td className="tabular-nums">{g.contact_phone || <span className="text-neutral-400">—</span>}</Td>
                  <Td><YesNo value={g.settlement_enabled} /></Td>
                  <Td><EntityStatus status={g.status} /></Td>
                  <Td className="tabular-nums">{formatDate(g.created_at)}</Td>
                  <Td align="right">
                    <div className="flex justify-end gap-0.5">
                      <IconButton icon={Eye} label="View details" onClick={() => setViewing(g)} />
                      <IconButton icon={Pencil} label="Edit group" onClick={() => setEditing(g)} />
                      <IconButton icon={Power} label={g.status === "active" ? "Deactivate" : "Activate"} onClick={() => toggle.request(g)} className={g.status === "active" ? "hover:!text-red-600" : "hover:!text-green-700"} />
                    </div>
                  </Td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </TableCard>

      <GroupFormModal
        open={editing !== undefined}
        group={editing}
        onClose={() => setEditing(undefined)}
        onSaved={(g) => {
          onGroupSaved?.(g);
          refetch();
        }}
      />

      <Overlay
        open={Boolean(viewing)}
        onClose={() => setViewing(null)}
        variant="drawer"
        width="max-w-lg"
        title={viewing ? `Group ${viewing.group_id}` : ""}
        subtitle={viewing?.group_name}
        footer={
          viewing && (
            <Button
              variant="primary"
              icon={Pencil}
              onClick={() => {
                setEditing(viewing);
                setViewing(null);
              }}
            >
              Edit group
            </Button>
          )
        }
      >
        {viewing && (
          <DetailGrid
            items={[
              ["Group ID", viewing.group_id],
              ["Status", <EntityStatus status={viewing.status} />],
              ["Group name", viewing.group_name, true],
              ["Owner", viewing.owner_name],
              ["Settlement", <YesNo value={viewing.settlement_enabled} />],
              ["Contact email", viewing.contact_email],
              ["Contact phone", viewing.contact_phone],
              ["Locations", viewing.location_count],
              ["Charging stations", viewing.station_count],
              ["Created", formatDate(viewing.created_at)],
            ]}
          />
        )}
      </Overlay>

      <ConfirmDialog {...toggle.dialogProps} />
    </div>
  );
}
